<script>import { getContext, onMount, onDestroy, setContext } from "svelte";
import TileLayer from "../../components/map/TileLayer.svelte";
import { WindFieldTileLayer } from "./windfield-layer.js";
import { WindVectorFactory } from "./wind-vector-factory.js";
import {
  WindFieldWorkerAPIFactory,
  distributionPositionIterator,
  tilePositionIterator
} from "./windfield.js";
import { WebMercator } from "../../map/projection.js";
import { bin2color, Color } from "../../d3-mapp/color/color.js";
import {
  mapContextKey
} from "../../components/map/context.js";
import { createSVGElement } from "../../util/dom-util.js";
import WindVectorLayer from "./WindVectorLayer.svelte";
import { writable } from "svelte/store";
export let worker;
export let hpc;
export let model;
export let profile;
export let time = void 0;
export let host = void 0;
export let domain;
export let credentials = void 0;
export let enableWindVector = true;
export let legacyMap = void 0;
export let legacyMapWrapper = void 0;
export let legacyLogging = false;
if (!getContext(mapContextKey) && legacyMapWrapper) {
  let context2 = {
    map: writable(legacyMap),
    mapImplementationWrapper: legacyMapWrapper,
    logger: {
      clear: () => console.clear(),
      log: (content) => {
        if (legacyLogging)
          console.log(content);
      }
    },
    enableLogging: legacyLogging
  };
  setContext(mapContextKey, context2);
}
const context = getContext(mapContextKey);
const { map, mapImplementationWrapper, logger, enableLogging } = context;
$: {
  if (enableLogging)
    logger.log("Set legacyMap with map " + !!legacyMap);
  map.set(legacyMap);
}
let mounted = false;
let tileLayer;
let workerApi;
let workerInitialized;
let workerReady = false;
let renderParameters;
let wvf;
let authenticationKey;
let gridTimeRange = void 0;
let distribution = void 0;
let windVectors = [];
let windFieldBuffer = void 0;
let layerResolution;
let latLngOfInterest = void 0;
let windVector = void 0;
const cursorOffset = { x: 50, y: -50 };
let latLangTrackerKey;
let enableAnimation = false;
onMount(async () => {
  workerApi = WindFieldWorkerAPIFactory(
    worker,
    //      new WindFieldWorker(),
    notificationHandlers,
    host
  );
  workerApi.enableLogging(enableLogging);
  mounted = true;
});
onDestroy(() => {
  if ($map && mapImplementationWrapper && latLangTrackerKey) {
    mapImplementationWrapper.removeLatLngTracker($map, latLangTrackerKey);
  }
});
$:
  $map && (latLangTrackerKey = mapImplementationWrapper.addLatLngTracker(
    $map,
    (latLng) => {
      if (latLng && layerResolution) {
        const mousePoint = WebMercator.latLngToPoint(latLng, layerResolution);
        latLngOfInterest = WebMercator.pointToLatLng(
          {
            x: mousePoint.x + cursorOffset.x,
            y: mousePoint.y + cursorOffset.y
          },
          layerResolution
        );
      } else {
        latLngOfInterest = void 0;
      }
    }
  ));
$:
  authenticateWorker(mounted, workerApi, domain, credentials);
$:
  getRenderParameters(workerApi);
$:
  initializeWindFieldWorker(mounted, authenticationKey, hpc, model, $map);
$:
  updateWindFieldProfile(workerInitialized, profile);
$:
  updateWindField(
    enableAnimation,
    workerReady,
    distribution,
    gridTimeRange,
    time
  );
$:
  updateWindVectorOfInterest(
    time,
    latLngOfInterest,
    enableWindVector
  );
const getRenderParameters = async (workerApi2) => {
  if (workerApi2) {
    renderParameters = await workerApi2.getRenderParameters();
    wvf = WindVectorFactory(
      renderParameters.color,
      renderParameters.fontSize,
      renderParameters.maxChars,
      renderParameters.cellSize.x,
      //assumes square
      renderParameters.maxArrow,
      renderParameters.windLow,
      renderParameters.windHigh,
      renderParameters.lightnessTrackLength
    );
    if (enableLogging)
      logger.log("Wind field render parameters obtained");
  }
};
const authenticateWorker = async (mounted2, workerApi2, domain2, credentials2) => {
  if (mounted2 && workerApi2) {
    const userSettings = await workerApi2.authenticate(domain2, credentials2);
    authenticationKey = {
      user: userSettings.id,
      domain: userSettings.domainId
    };
    if (enableLogging)
      logger.log(
        `Authenticated WindFieldWorker at domain ${domain2}. authenticationKey=${JSON.stringify(authenticationKey)}`
      );
  }
};
const initializeWindFieldWorker = async (mounted2, authenticationKey2, hpc2, model2, map2) => {
  workerInitialized = false;
  if (enableLogging)
    logger.log(
      `initializeWindFieldWorker called mounted:${mounted2} authenticationKey:${JSON.stringify(authenticationKey2)} hpc:${hpc2} model:${model2} map:${!!map2}`
    );
  if (mounted2 && authenticationKey2 && map2) {
    await workerApi.init(hpc2, model2);
    if (enableLogging)
      logger.log(
        `WindFieldWorker initialized for hpc ${hpc2} with model ${model2}`
      );
    getWindFieldTileLayer();
    workerInitialized = true;
  }
};
const updateWindFieldProfile = async (workerInitialized2, profile2) => {
  if (workerInitialized2 && profile2) {
    if (enableLogging)
      logger.log("Loading profile...");
    enableAnimation = false;
    workerApi.set(profile2);
  }
};
const updateWindVectorOfInterest = (time2, latLng, enableWindVector2) => {
  if (enableWindVector2 && time2 !== void 0 && latLng) {
    workerApi.getVector(time2, latLng).then((uv) => {
      if (uv && uv.u != void 0) {
        windVector = uv;
      } else {
        windVector = void 0;
      }
    });
  } else {
    windVector = void 0;
  }
};
const updateWindField = async (enableAnimation2, workerReady2, distribution2, gridTimeRange2, time2) => {
  if (enableAnimation2 && workerReady2 && distribution2 && gridTimeRange2) {
    if (time2 !== void 0 && gridTimeRange2.start <= time2 && gridTimeRange2.end >= time2) {
      try {
        windFieldBuffer = await workerApi?.getField(
          time2,
          windFieldBuffer
        );
        renderWindField(windFieldBuffer);
        if (enableLogging)
          logger.log("render called for " + (time2 ? new Date(time2).toISOString() : time2) + ": SHOULD HAVE RENDERED");
      } catch (error) {
        if (enableLogging)
          logger.log("render called for " + (time2 ? new Date(time2).toISOString() : time2) + ": IN ERROR");
      }
    } else {
      if (enableLogging)
        logger.log("render called for " + (time2 ? new Date(time2).toISOString() : time2) + ": OUT OF RANGE");
      renderUndefinedWindField();
    }
  } else {
    if (enableLogging)
      logger.log("render called for " + (time2 ? new Date(time2).toISOString() : time2) + `: NOT READYenableAnimation=${enableAnimation2} workerReady=${workerReady2}  distribution=${!!distribution2}  gridTimeRange=${!!gridTimeRange2} `);
  }
};
const notificationHandlers = {
  log: async (_send, _transfer, content) => {
    if (enableLogging)
      logger.log(content);
  },
  cacheUpdated: (send, _transfer, cacheKey) => {
    if (enableLogging)
      logger.log("Loaded interval " + cacheKey);
    send({ status: "ok", key: cacheKey });
  },
  cacheComplete: async (send, _transfer, timeRange, gridProperties, ready) => {
    workerReady = ready;
    gridTimeRange = timeRange;
    if (enableLogging)
      logger.log("Loading wind profile complete.");
    if (enableLogging)
      logger.log(
        "Grid time range " + (timeRange ? `{ start: ${new Date(timeRange.start).toISOString()}, end: ${new Date(timeRange.end).toISOString()} }` : void 0)
      );
    enableAnimation = !!distribution;
    send({ status: "ok" });
  },
  positions: async (_send, _transfer, newDistribution) => {
    distribution = void 0;
    windFieldBuffer = void 0;
    windVectors = [];
    Object.keys(newDistribution).sort().forEach((tileKey) => {
      const tiles = getWindFieldTileLayer().getTileElements(tileKey);
      if (tiles.length > 0 && tiles[0]) {
        const tileElem = tiles[0];
        tileElem.querySelectorAll("g.wind-vector").forEach((el) => {
          el.parentNode?.removeChild(el);
        });
        tileElem.querySelectorAll("g.wind-vector-debug").forEach((el) => {
          el.parentNode?.removeChild(el);
        });
        for (let position of tilePositionIterator(
          newDistribution[tileKey].positions
        )) {
          const windVector2 = createWindVector(position);
          tileElem.appendChild(windVector2);
          windVectors.push(windVector2);
        }
      } else {
        if (enableLogging)
          logger.log(`Tile ${tileKey} has been removed by layer.`);
        newDistribution[tileKey].positions.forEach(
          (position) => position.removed = true
        );
      }
    });
    distribution = newDistribution;
    enableAnimation = true;
  }
};
const onTileAction = (tileProperties, action) => {
  if (workerApi) {
    switch (action) {
      case "add":
        workerApi.addTile(tileProperties);
        break;
      case "remove":
        workerApi.removeTile(tileProperties);
        break;
    }
  }
};
const onTileLayerAction = (zoom, resolution, tileSize, action) => {
  layerResolution = resolution;
  if (workerApi) {
    switch (action) {
      case "ready":
        enableAnimation = false;
        workerApi.tilesReady(zoom, resolution, tileSize);
        break;
    }
  }
};
const getWindFieldTileLayer = () => {
  if (!tileLayer) {
    tileLayer = new WindFieldTileLayer(
      mapImplementationWrapper.createTileLayer(),
      onTileAction,
      onTileLayerAction
    );
  }
  return tileLayer;
};
const createWindVector = (p) => {
  const displayWindSpeed = renderParameters.detailLevel > 0 ? p.index.x % renderParameters.detailLevel === 0 && p.index.y % renderParameters.detailLevel === 0 : false;
  const gElem = createSVGElement("g", document);
  gElem.classList.add("wind-vector");
  gElem.setAttribute("style", `--fontsize:${renderParameters.fontSize}px;`);
  gElem.setAttribute("role", "tooltip");
  gElem.innerHTML = `<path
      d=""
      fill="transparent"
    />
  <circle
    cx="${p.tile.x}"
    cy="${-p.tile.y}"
    r="${displayWindSpeed ? wvf.radiusMax() : wvf.radiusMin()}"
    stroke="${"gray"}"
    stroke-width="${wvf.stroke()}"
    fill="${displayWindSpeed ? "white" : "gray"}"
  />
  ${displayWindSpeed ? `
    <text
      text-anchor="middle"
      x="${p.tile.x}"
      y="${-p.tile.y}"
      dy="${wvf.labelDY()}"
      fill="${"gray"}">${"?"}</text
    >` : ""}`;
  return gElem;
};
const createDebugWindVector = (p) => {
  const gElem = createSVGElement("g", document);
  gElem.classList.add("wind-vector-debug");
  gElem.innerHTML = `<circle r="${50}"" cx="${p.tile.x}" cy="${-p.tile.y}" fill="#ff800080"/><text fill="#000000" x="${p.tile.x - 10}" y="${-(p.tile.y + 30)}">${p.index.x}, ${p.index.y}</text><text fill="#000000" x="${p.tile.x - 10}" y="${-(p.tile.y + (p.index.x % 2 === 0 ? 20 : -30))}">${p.utm.x}, ${p.utm.y}</text>`;
  return gElem;
};
const renderWindField = (dataView) => {
  let windVectorNode;
  let indexX;
  let indexY;
  let ws;
  let label;
  let color;
  let colorHex;
  let innerColor;
  let innerColorHex;
  let c0x, c0y, c1x, c1y, c2x, c2y;
  let d;
  let pathFill;
  let dws;
  let path;
  let circle;
  let text;
  if (dataView && distribution) {
    const _distribution = distribution;
    let iVector = 0;
    let iBuf = 0;
    for (let position of distributionPositionIterator(_distribution)) {
      indexX = dataView.getUint16(iBuf);
      iBuf += Uint16Array.BYTES_PER_ELEMENT;
      indexY = dataView.getUint16(iBuf);
      iBuf += Uint16Array.BYTES_PER_ELEMENT;
      ws = dataView.getFloat32(iBuf);
      iBuf += Float32Array.BYTES_PER_ELEMENT;
      color = bin2color(dataView.buffer, iBuf);
      iBuf += 4 * Uint8Array.BYTES_PER_ELEMENT;
      innerColor = bin2color(dataView.buffer, iBuf);
      iBuf += 4 * Uint8Array.BYTES_PER_ELEMENT;
      c0x = dataView.getFloat32(iBuf);
      iBuf += Float32Array.BYTES_PER_ELEMENT;
      c0y = dataView.getFloat32(iBuf);
      iBuf += Float32Array.BYTES_PER_ELEMENT;
      c1x = dataView.getFloat32(iBuf);
      iBuf += Float32Array.BYTES_PER_ELEMENT;
      c1y = dataView.getFloat32(iBuf);
      iBuf += Float32Array.BYTES_PER_ELEMENT;
      c2x = dataView.getFloat32(iBuf);
      iBuf += Float32Array.BYTES_PER_ELEMENT;
      c2y = dataView.getFloat32(iBuf);
      iBuf += Float32Array.BYTES_PER_ELEMENT;
      if (!position.removed) {
        windVectorNode = windVectors[iVector];
        dws = renderParameters.detailLevel > 0 ? position.index.x % renderParameters.detailLevel === 0 && position.index.y % renderParameters.detailLevel === 0 : false;
        if (windVectorNode) {
          label = (ws > 0 ? ws : 0).toFixed(ws > 100 ? 0 : ws >= 1 ? 1 : 2);
          colorHex = color.formatHex();
          innerColorHex = innerColor.formatHex();
          d = ws > renderParameters.wsZeroThreshold ? `M ${c0x} ${-c0y} L ${c1x} ${-c1y} L ${c2x} ${-c2y} Z` : "";
          pathFill = ws > renderParameters.wsZeroThreshold ? colorHex : "transparent";
          path = windVectorNode.children[0];
          path.setAttribute("d", d);
          path.setAttribute("fill", pathFill);
          circle = windVectorNode.children[1];
          circle.setAttribute("stroke", colorHex);
          circle.setAttribute("fill", dws ? innerColorHex : colorHex);
          circle.setAttribute("r", `${dws ? wvf.radiusMax() : wvf.radiusMin()}`);
          if (dws) {
            text = windVectorNode.children[2];
            text.setAttribute("fill", colorHex);
            text.textContent = label;
          }
        }
      }
      iVector++;
    }
  }
};
const renderUndefinedWindField = () => {
  let windVectorNode;
  let label = "?";
  let colorHex = "gray";
  let innerColorHex = "white";
  let d = "";
  let pathFill = "transparent";
  let dws = false;
  let path;
  let circle;
  let text;
  if (distribution) {
    const _distribution = distribution;
    let iVector = 0;
    for (let position of distributionPositionIterator(_distribution)) {
      if (!position.removed) {
        windVectorNode = windVectors[iVector];
        if (windVectorNode) {
          path = windVectorNode.children[0];
          path.setAttribute("d", d);
          path.setAttribute("fill", pathFill);
          circle = windVectorNode.children[1];
          circle.setAttribute("stroke", colorHex);
          circle.setAttribute("fill", dws ? innerColorHex : colorHex);
          circle.setAttribute("r", "1");
          if (dws) {
            text = windVectorNode.children[2];
            text.setAttribute("fill", colorHex);
            text.textContent = label;
          }
        }
      }
      iVector++;
    }
  }
};
</script>

<div class="wind-field-layer">
  {#if $map && tileLayer}
    <TileLayer {tileLayer} />
    {#if enableWindVector}
      <WindVectorLayer
        {windVector}
        {wvf}
        wsZeroThreshold={renderParameters?.wsZeroThreshold}
      />
    {/if}
  {/if}
</div>
